import { useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import {
  flushOutbox,
  hasPendingOutboxEntries,
  listOutbox,
  subscribeOutbox,
  type OutboxEntry,
} from '../lib/outbox';

/** The live outbox — re-renders on any queue change (this tab or another). */
export function useOutboxEntries(): OutboxEntry[] {
  const [entries, setEntries] = useState<OutboxEntry[]>(() => listOutbox());
  useEffect(() => subscribeOutbox(() => setEntries(listOutbox())), []);
  return entries;
}

/** The live outbox entry for one document path, if any. */
export function useOutboxEntry(path: string): OutboxEntry | null {
  const entries = useOutboxEntries();
  return entries.find((e) => e.path === path) ?? null;
}

// Retry cadence while entries wait. HISTORY taught us navigator.onLine lies
// on iOS, so the `online` event is a hint, not the mechanism — the flush
// itself is the connectivity probe, and the interval is the backstop.
const RETRY_INTERVAL_MS = 30_000;

/**
 * Mounted ONCE in the authenticated shell (Layout): replays the offline
 * outbox on startup, when the browser reports connectivity back, when the
 * app returns to the foreground, and on a slow retry interval while entries
 * are waiting. `flushOutbox` itself is reentrancy-safe.
 */
export function useOutboxSync(): void {
  const client = useApolloClient();
  useEffect(() => {
    const flush = () => {
      if (hasPendingOutboxEntries()) void flushOutbox(client);
    };
    flush();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') flush();
    };
    window.addEventListener('online', flush);
    document.addEventListener('visibilitychange', onVisibility);
    const interval = window.setInterval(flush, RETRY_INTERVAL_MS);
    return () => {
      window.removeEventListener('online', flush);
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(interval);
    };
  }, [client]);
}
